// @flow
import * as React from 'react';
import {useEffect, useState, useReducer} from 'react';

import {useApi} from 'docs/data';

import Box from 'sail/Box';
import Card from 'sail/Card';
import Group from 'sail/Group';
import Text from 'sail/Text/Text';
import Spinner from 'sail/Spinner';
import Icon from 'sail/Icon';
import {Caption} from 'sail/Text';

import {Elements, PaymentElement} from '@stripe/react-stripe-js';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import {THEMES, LOCATIONS, SIZES} from './constants';
import {LocationConfig, ThemeConfig, SizeConfig} from './configDropdowns';
import {reducer, getInitialState} from './state';
import type {State} from './state';

const messages = defineMessages({
  unexpectedError: {
    id: 'docsApp.demo.paymentElement.unexpectedError',
    description: 'Unexpected error message',
    defaultMessage: "An error occurred. We're looking into it!",
  },
  walletAvailability: {
    id: 'docsApp.demo.paymentElement.walletAvailability',
    description: 'Text explain wallet payment method availability',
    defaultMessage:
      'The demo displays Google Pay or Apple Pay only on their corresponding platform if you have an active card associated with the account.',
  },
});

const UNEXPECTED_ERROR_MESSAGE = messages.unexpectedError;

const InnerPaymentElementDemo = ({state}: {|state: State|}) => (
  <>
    <Box
      className={'PaymentElement-demo'}
      style={{
        maxWidth: SIZES[state.size].value,
      }}
      flexBasis="100%"
      padding={12}
      margin={{vertical: 64}}
    >
      <PaymentElement
        options={{
          paymentMethodOrder: LOCATIONS[state.location].paymentMethodOrder,
          business: {
            name: 'Stripe Payment Element Demo',
          },
        }}
      />
    </Box>
  </>
);

export const PaymentElementDemo = () => {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const {api} = useApi();

  const intl = useIntl();
  const localizedErrorMessage = intl.formatMessage(UNEXPECTED_ERROR_MESSAGE);

  useEffect(() => {
    if (state.clientSecret) {
      return;
    }

    api
      .createPaymentIntent({
        payment_method_types: LOCATIONS[state.location].paymentMethodTypes,
        is_payment_element: true,
        payment_element_currency: LOCATIONS[state.location].currency,
      })
      .then((response) => {
        if (!response.clientSecret) {
          const domain = window.location.hostname || 'stripe.com';
          const errorMessage = domain.match('dev.stripe.me')
            ? 'This demo does not work locally.'
            : localizedErrorMessage;

          setError(errorMessage);
          setLoading(false);
          return;
        }
        dispatch({kind: 'SECRET_CHANGED', clientSecret: response.clientSecret});
        setLoading(false);
      });
  }, [api, state.location, state.clientSecret, localizedErrorMessage]);

  const elementsKey = `${state.location})}`;

  const options = {
    locale: LOCATIONS[state.location].locale,
    clientSecret: state.clientSecret,
    appearance: THEMES[state.theme].appearance,
  };

  return (
    <Box margin={{vertical: 20, horizontal: 12}} style={{borderRadius: '4px'}}>
      <Card shadow="keyline">
        <Group
          direction="horizontal"
          spacing={20}
          justifyContent="center"
          alignItems="center"
          className="PaymentElementDemo-navbar"
          wrap="wrap"
          divider={{color: 'light', width: 1, side: 'bottom'}}
          padding={12}
        >
          <LocationConfig state={state} dispatch={dispatch} />
          <SizeConfig state={state} dispatch={dispatch} />
          <ThemeConfig state={state} dispatch={dispatch} />
        </Group>
        <Box
          flex={{
            justifyContent: 'center',
            alignItems: error ? 'center' : 'flex-start',
          }}
          background={THEMES[state.theme].background}
          style={{minHeight: '500px'}}
        >
          {loading && (
            <Box style={{position: 'absolute'}}>
              <Spinner size="large" style={{padding: '200px'}} />
            </Box>
          )}
          {state.clientSecret && (
            <Elements
              key={elementsKey}
              stripe={state.stripePromise}
              options={options}
            >
              <InnerPaymentElementDemo state={state} />
            </Elements>
          )}
          {error && (
            <Box style={{position: 'absolute', padding: '200px'}}>
              <Group direction="horizontal" spacing={8}>
                <Icon icon="warning" color="gray" size={14} />
                <Text size={15} color="gray600">
                  {error}
                </Text>
              </Group>
            </Box>
          )}
        </Box>
      </Card>
      <Caption color="gray500" size="13px">
        {<FormattedMessage {...messages.walletAvailability} />}
      </Caption>
    </Box>
  );
};
